/**
 * The Export button: every file a table can be exported as, in one menu.
 *
 * It replaces a single "Export CSV" link that could only say "everything"
 * (issue #56). What it adds is **what the filter shows**: tables are searched
 * and sliced in the browser, so "export what I am looking at" cannot be a URL -
 * the server never saw the filter. The rows on screen are sent as ids instead
 * (`POST .../export`), which is why those items are downloads made from code
 * and the "All" ones are plain links.
 *
 * ONE FILE PER SCOPE, for every kind but requirements. The generic export
 * already carries the type, the element ID and the change stamp (who last
 * changed each row, and when) for every kind, so a second "with details" file
 * would differ by a single column and be clutter. Don't add one back.
 *
 * REQUIREMENTS ARE THE ODD ONE, and the only kind with two files. Their plain
 * export is the RTM - the traceability matrix, one row per requirement with its
 * verification chain - which is a different shape from the generic listing, not
 * a narrower copy of it. So they also get the generic export as "every field",
 * and `rich` there adds the qualified name to it. Same menu, different files
 * behind the items; `standard` says which.
 *
 * THE RVTM is the one screen whose plain file is built in the browser (issue
 * #100): `matrixFile` supplies it, for the whole program or for the rows shown,
 * because the cell states are worked out here and a server copy would be a second
 * implementation. The matrix then takes the place of the RTM as the plain file,
 * and the RTM rows follow as "RTM rows" items, without the every-field file.
 *
 * The "shown" items appear ONLY when the table is narrowed and the host knows
 * which rows are on screen. A host that cannot say (EntityList and States do not
 * know what their table shows) gets the "All" items and nothing wrong: an item
 * offering "the 12 shown" over a guess would export a different 12 than the
 * person is looking at. They are also left out when the narrowing matches
 * nothing, since an empty file is not an export anyone asked for.
 */

import { useState } from 'react'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DownloadIcon from '@mui/icons-material/Download'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { api, saveBlob } from '../../api'
import { useToasts } from '../../state/Toasts'

export interface ExportMenuProps {
  /** The kind's plural, for the wording and for the address: 'requirements'. */
  plural: string
  /**
   * What the plain file is. `'rtm'` for requirements (the matrix); the generic
   * entity listing otherwise.
   */
  standard?: 'entities' | 'rtm'
  /**
   * The row ids the table is showing, in order, or nothing when the host cannot
   * say. Only used when `filtered`.
   */
  shownIds?: string[] | null
  /** Whether the table is narrowed at all. Unfiltered, "the shown" IS "all". */
  filtered?: boolean
  /**
   * The verification matrix, built by the host from the rows it holds (issue
   * #100). Asked for the scope it is for, it answers the file's text and the
   * name it is saved under. When given, it is the plain file and `standard`
   * says which RTM items follow it.
   */
  matrixFile?: (scope: 'all' | 'shown') => { csv: string; name: string }
}

const ALL_FIELDS_NOTE =
  'Every field of each requirement, with its type, element ID, qualified name and who last changed it.'

const MATRIX_NOTE =
  'The verification matrix: each cell’s state in words, and the cases behind it, as the screen draws it.'

const RTM_NOTE = 'One row per requirement, with its verification chain.'

export default function ExportMenu({
  plural,
  standard = 'entities',
  shownIds = null,
  filtered = false,
  matrixFile,
}: ExportMenuProps) {
  const toasts = useToasts()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  /**
   * Which POST item is in flight, so that one is disabled while it runs.
   *
   * Held rather than derived from the click: a second click on the same item
   * would otherwise start a second download of a file already on its way.
   */
  const [busy, setBusy] = useState<string | null>(null)

  const matrix = standard === 'rtm'
  const close = () => setAnchor(null)
  const ids = filtered && shownIds && shownIds.length > 0 ? shownIds : null

  /** Save the host's matrix for one scope, as a file made here in the browser. */
  const saveMatrix = (scope: 'all' | 'shown') => {
    const file = matrixFile?.(scope)
    return file ? saveBlob(new Blob([file.csv], { type: 'text/csv' }), file.name) : Promise.resolve()
  }

  /**
   * Run one download; on failure say so and keep the menu open.
   *
   * Left open on purpose: the person's next move after a refused export is
   * usually the other item, or the same one again.
   */
  const run = (key: string, download: () => Promise<void>) => {
    setBusy(key)
    download()
      .then(close)
      .catch((problem: unknown) =>
        toasts.failed(
          `Export failed: ${problem instanceof Error ? problem.message : String(problem)}`,
        ),
      )
      .finally(() => setBusy(null))
  }

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        endIcon={<ArrowDropDownIcon />}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        Export
      </Button>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={close}>
        {matrixFile && (
          <MenuItem
            disabled={busy === 'matrix'}
            onClick={() => run('matrix', () => saveMatrix('all'))}
          >
            <ListItemText primary={`All ${plural}`} secondary={MATRIX_NOTE} />
          </MenuItem>
        )}
        {matrixFile && ids && (
          <MenuItem
            disabled={busy === 'matrix-shown'}
            onClick={() => run('matrix-shown', () => saveMatrix('shown'))}
          >
            <ListItemText primary={`The ${ids.length} shown`} secondary={MATRIX_NOTE} />
          </MenuItem>
        )}
        {matrixFile && <Divider />}

        <MenuItem
          component="a"
          href={matrix ? api.rtmCsvUrl() : api.entityCsvUrl(plural)}
          download={matrix ? 'rtm.csv' : `${plural}.csv`}
          onClick={close}
        >
          <ListItemText
            primary={matrixFile ? `RTM rows, all ${plural}` : `All ${plural}`}
            secondary={matrix ? (matrixFile ? RTM_NOTE : 'The traceability matrix.') : undefined}
          />
        </MenuItem>
        {matrix && !matrixFile && (
          <MenuItem
            component="a"
            href={api.entityRichCsvUrl(plural)}
            download={`${plural}-all-fields.csv`}
            onClick={close}
          >
            <ListItemText primary={`All ${plural}, every field`} secondary={ALL_FIELDS_NOTE} />
          </MenuItem>
        )}

        {ids && (
          <MenuItem
            disabled={busy === 'shown'}
            onClick={() =>
              run('shown', () =>
                matrix
                  ? api.exportRtm(ids, 'rtm-shown.csv')
                  : api.exportEntities(plural, ids, false, `${plural}-shown.csv`),
              )
            }
          >
            <ListItemText
              primary={matrixFile ? `RTM rows, the ${ids.length} shown` : `The ${ids.length} shown`}
              secondary={matrix ? (matrixFile ? RTM_NOTE : 'The traceability matrix.') : undefined}
            />
          </MenuItem>
        )}
        {ids && matrix && !matrixFile && (
          <MenuItem
            disabled={busy === 'shown-all-fields'}
            onClick={() =>
              run('shown-all-fields', () =>
                api.exportEntities(plural, ids, true, `${plural}-shown-all-fields.csv`),
              )
            }
          >
            <ListItemText primary={`The ${ids.length} shown, every field`} secondary={ALL_FIELDS_NOTE} />
          </MenuItem>
        )}
      </Menu>
    </>
  )
}
